// src/pages/admin/AdminNotificationsPage.tsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FullScreenLoader } from "components/ui/Loader";
import { getErrorMessage, isVersionConflict, conflictCurrentVersion } from "utils/errors";
import {
  adminListNotifications,
  adminRetryNotification,
  adminAcknowledgeNotification,
  NotificationOut,
  PagedNotifications
} from "api/admin";

import NotificationFilters from "features/admin/components/NotificationFilters";
import NotificationRow from "features/admin/components/NotificationRow";
import NotificationDetailModal from "features/admin/components/NotificationDetailModal";

type LineGeom = {
  left: number;
  titleTop: number;
  titleBottom: number;
  containerTop: number;
  containerBottom: number;
};

const DEFAULT_PAGE_SIZE = 25;
const FETCH_PAGE_SIZE = 200;
const GOLD = "#f3dfae";

const ALL_STATUSES = ["queued", "sending", "failed", "sent", "acknowledged"] as const;

const safeStringify = (v: any) => {
  try {
    return JSON.stringify(v);
  } catch {
    return "";
  }
};

const AdminNotificationsPage: React.FC = () => {
  const [allItems, setAllItems] = useState<NotificationOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string>("");

  // Local pagination (applies after local filtering)
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  // Local filters
  const [statuses, setStatuses] = useState<string[]>([...ALL_STATUSES]);
  const [query, setQuery] = useState<string>("");

  const [detailId, setDetailId] = useState<number | null>(null);
  const isModalOpen = detailId != null;

  // Title/container vertical line geometry (CookiesPage logic)
  const pageRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [geom, setGeom] = useState<LineGeom | null>(null);

  const fetchAllNotifications = async () => {
    setErr("");
    setLoading(true);
    try {
      const acc: NotificationOut[] = [];
      let p = 1;
      let total = Infinity;

      // Fetch all pages without status/query filters.
      while (acc.length < total) {
        // eslint-disable-next-line no-await-in-loop
        const res: PagedNotifications = await adminListNotifications({
          page: p,
          page_size: FETCH_PAGE_SIZE
        });

        const chunk = res.items || [];
        total = typeof res.total === "number" ? res.total : acc.length + chunk.length;

        acc.push(...chunk);

        if (chunk.length === 0) break; // safety: no more results
        p += 1;
        if (p > 500) break; // safety: prevent infinite loops
      }

      setAllItems(acc);
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to load notifications"));
      setAllItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchAllNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();

    return allItems.filter((n) => {
      if (statuses.length > 0 && !statuses.includes(String(n.status))) return false;
      if (!q) return true;

      const nn: any = n; // allow searching optional/untyped fields without breaking TS

      const haystack = [
        n.status,
        n.kind,
        n.order_id,
        n.user_email,
        n.clinic_name,
        n.last_error,
        nn.package,
        nn.user_name,
        nn.contact_email,
        nn.contact_phone,
        safeStringify(nn.payload)
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [allItems, statuses, query]);

  const total = filteredItems.length;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [statuses.join(","), query]);

  // Clamp page if totalPages shrinks
  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  useLayoutEffect(() => {
    const pageEl = pageRef.current;
    const titleEl = titleRef.current;
    const containerEl = containerRef.current;
    if (!pageEl || !titleEl || !containerEl) return;

    const compute = () => {
      const p = pageEl.getBoundingClientRect();
      const t = titleEl.getBoundingClientRect();
      const c = containerEl.getBoundingClientRect();

      const left = Math.round(t.left + t.width / 2 - p.left);

      setGeom({
        left: Math.max(0, left),
        titleTop: Math.max(0, Math.round(t.top - p.top)),
        titleBottom: Math.max(0, Math.round(t.bottom - p.top)),
        containerTop: Math.max(0, Math.round(c.top - p.top)),
        containerBottom: Math.max(0, Math.round(c.bottom - p.top))
      });
    };

    compute();

    const ro = new ResizeObserver(() => compute());
    ro.observe(pageEl);
    ro.observe(titleEl);
    ro.observe(containerEl);

    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [pageItems.length, loading, err, total, page, pageSize, statuses.join(","), query]);

  const onRetry = async (n: NotificationOut) => {
    setErr("");
    try {
      const updated = await adminRetryNotification(n.id, n.lock_version);
      setAllItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (e: any) {
      if (isVersionConflict(e)) {
        const current = conflictCurrentVersion(e);
        setErr(
          current ? `Stale update (current v${current}). Refreshing…` : "Version conflict. Refreshing…"
        );
        await fetchAllNotifications();
      } else {
        setErr(getErrorMessage(e, "Failed to retry"));
      }
    }
  };

  const onAcknowledge = async (n: NotificationOut) => {
    setErr("");
    try {
      const updated = await adminAcknowledgeNotification(n.id, n.lock_version);
      setAllItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (e: any) {
      if (isVersionConflict(e)) {
        const current = conflictCurrentVersion(e);
        setErr(
          current ? `Stale update (current v${current}). Refreshing…` : "Version conflict. Refreshing…"
        );
        await fetchAllNotifications();
      } else {
        setErr(getErrorMessage(e, "Failed to acknowledge"));
      }
    }
  };

  const TITLE_GAP_PX = 14;
  const CONTAINER_TOP_MARGIN_CLASS = "mt-16 sm:mt-20";

  return (
    <main ref={pageRef} className="relative min-h-screen px-4 py-12 sm:py-16">
      {geom && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: 0,
              bottom: `calc(100% - ${Math.max(0, geom.titleTop - TITLE_GAP_PX)}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.titleBottom + TITLE_GAP_PX,
              bottom: `calc(100% - ${geom.containerTop}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.containerBottom,
              bottom: 0
            }}
          />
        </>
      )}

      <div
        className={[
          "relative z-10 transition-[filter] duration-200",
          isModalOpen ? "blur-sm" : ""
        ].join(" ")}
        aria-hidden={isModalOpen ? "true" : "false"}
      >
        <header className="text-center">
          <h1
            ref={titleRef}
            className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#ceddf2]"
          >
            Admin · Notifications
          </h1>
        </header>

        <section className={[CONTAINER_TOP_MARGIN_CLASS, "flex justify-center"].join(" ")}>
          <div
            ref={containerRef}
            className="w-full md:w-3/5 bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-8 sm:px-8"
          >
            <div className="flex items-start justify-between gap-4">
              <Link
                to="/admin"
                className="font-ui text-xs uppercase tracking-wide text-slate-500 hover:opacity-80"
              >
                ← Back to Admin Home
              </Link>

              <button
                type="button"
                onClick={() => fetchAllNotifications()}
                className={[
                  "inline-flex items-center justify-center rounded-full",
                  "border border-slate-200 bg-white",
                  "px-5 py-2.5",
                  "text-xs uppercase tracking-wide text-[#151A3A]",
                  "hover:bg-slate-50 transition-colors font-ui font-normal"
                ].join(" ")}
              >
                Refresh
              </button>
            </div>

            <div className="mt-6 rounded-2xl border p-5" style={{ borderColor: `${GOLD}B3` }}>
              <h2 className="font-ui font-normal uppercase tracking-wide text-slate-800">Filters</h2>
              <div className="mt-4">
                <NotificationFilters
                  initialStatus={statuses}
                  query={query}
                  onChange={(nextStatuses: string[], nextQuery: string) => {
                    setStatuses(nextStatuses);
                    setQuery(nextQuery);
                  }}
                />
              </div>
            </div>

            {loading ? (
              <div className="mt-8">
                <FullScreenLoader label="Loading…" />
              </div>
            ) : (
              <>
                {err && (
                  <div className="mt-6 border border-red-300 bg-red-50 text-red-700 p-3 rounded-2xl font-body text-sm">
                    {err}
                  </div>
                )}

                <div className="mt-8">
                  {pageItems.length === 0 ? (
                    <div className="font-body text-slate-600">No notifications found.</div>
                  ) : (
                    <div className="space-y-2">
                      {pageItems.map((row: NotificationOut) => (
                        <NotificationRow
                          key={row.id}
                          notification={row}
                          onRetry={onRetry}
                          onAcknowledge={onAcknowledge}
                          onView={(r: NotificationOut) => setDetailId(r.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between gap-3 border-t border-slate-200 pt-4">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className={[
                        "inline-flex items-center justify-center rounded-full",
                        "border border-slate-200 bg-white",
                        "px-5 py-2.5",
                        "text-xs uppercase tracking-wide text-[#151A3A]",
                        "hover:bg-slate-50 transition-colors font-ui font-normal",
                        "disabled:opacity-50"
                      ].join(" ")}
                    >
                      Prev
                    </button>

                    <div className="font-ui text-xs uppercase tracking-wide text-slate-500">
                      Page {page} / {totalPages} · {total} shown · {allItems.length} total
                    </div>

                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className={[
                        "inline-flex items-center justify-center rounded-full",
                        "border border-slate-200 bg-white",
                        "px-5 py-2.5",
                        "text-xs uppercase tracking-wide text-[#151A3A]",
                        "hover:bg-slate-50 transition-colors font-ui font-normal",
                        "disabled:opacity-50"
                      ].join(" ")}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      {detailId != null && (
        <NotificationDetailModal
          id={detailId}
          onClose={() => setDetailId(null)}
          onActionComplete={async () => {
            setDetailId(null);
            await fetchAllNotifications();
          }}
        />
      )}
    </main>
  );
};

export default AdminNotificationsPage;
